import {
  LitElement,
  broadcastReceiver,
  html,
  utils,
  animationHelpers,
} from '@hh/global.js'

class HHHeader extends LitElement {
  EXIT_TIMEOUT = 50
  HOVER_DELAY = 0

  static properties = {
    promoBar: { state: true },
  }

  constructor() {
    super()

    this.triggers = this.querySelectorAll('.js-header-mega-menu-trigger')
    this.menuItems = this.querySelectorAll('.js-header-mega-menu-panel')
    this.headerV2 = this.querySelector('.header-v2')
    this.hoverTimeout = null

    this.openMenu = this.openMenu.bind(this)
    this.closeMenu = this.closeMenu.bind(this)
    this.handleMenuItemEnter = this.handleMenuItemEnter.bind(this)
    this.handleMenuItemLeave = this.handleMenuItemLeave.bind(this)
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleLinkMouseEnter = this.handleLinkMouseEnter.bind(this)
    this.handleLinkMouseLeave = this.handleLinkMouseLeave.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.initPromoBar()
    this.handleHashedLinks()
    this.observeDarkModeHeader()

    this.receiver = broadcastReceiver('product-added', (...args) => {
      this.openCart()
    })

    for (const trigger of this.triggers) {
      if (!this.headerV2) {
        trigger.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.closeMenu(event)
        })
        trigger.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.openMenu(event)
        })
        trigger.addEventListener('pointerup', this.openMenu)
        trigger.addEventListener('click', (e) => {
          e.preventDefault()
          window.location.href = trigger.getAttribute('data-url')
        })
      } else {
        trigger.addEventListener('click', (event) => {
          event.preventDefault()
          this.openMenu(event)
        })
      }
    }

    if (!this.headerV2) {
      for (const menuItem of this.menuItems) {
        menuItem.addEventListener('pointerenter', this.handleMenuItemEnter)
        menuItem.addEventListener('pointerleave', this.handleMenuItemLeave)
      }
    }

    // Add keyboard listener for Escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeMenu() // Assuming closeMenu can handle being called without an event object
      }
    })

    document.querySelectorAll('[data-link-id]').forEach((link) => {
      link.addEventListener('mouseenter', this.handleLinkMouseEnter)
      link.addEventListener('mouseleave', this.handleLinkMouseLeave)
      link.addEventListener('focusin', this.handleLinkMouseEnter)
      link.addEventListener('focusout', this.handleLinkMouseLeave)
    })

    this.headerV2 &&
      document.addEventListener('click', this.handleDocumentClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    clearTimeout(this.dismissPromoBar)

    this.receiver?.disconnect()
    this.headerObserver?.disconnect()

    for (const trigger of this.triggers) {
      if (!this.headerV2) {
        trigger.removeEventListener('pointerleave', this.closeMenu)
        trigger.removeEventListener('pointerenter', this.openMenu)
        trigger.removeEventListener('pointerup', this.openMenu)
      } else {
        trigger.removeEventListener('click', this.openMenu)
      }
    }

    for (const menuItem of this.menuItems) {
      menuItem.removeEventListener('pointerenter', this.handleMenuItemEnter)
      menuItem.removeEventListener('pointerleave', this.handleMenuItemLeave)
    }

    this.headerV2 &&
      document.removeEventListener('click', this.handleDocumentClick)

    this.querySelectorAll('[data-link-id]').forEach((link) => {
      link.removeEventListener('mouseenter', this.handleLinkMouseEnter)
      link.removeEventListener('mouseleave', this.handleLinkMouseLeave)
      link.removeEventListener('focusin', this.handleLinkMouseEnter)
      link.removeEventListener('focusout', this.handleLinkMouseLeave)
    })
  }

  clearExitTimer() {
    try {
      clearTimeout(this.navLeaveTimer)
      this.navLeaveTimer = null
    } catch (error) {
      console.error(error)
    }
  }

  setExitTimer() {
    try {
      this.navLeaveTimer = setTimeout(() => {
        this.activeMenuItem = null
        this.applyState()
      }, this.EXIT_TIMEOUT)
    } catch (error) {
      console.error(error)
    }
  }

  handleLinkMouseEnter(event) {
    const linkElement = event.target.closest('[data-link-id]')
    if (linkElement) {
      const linkId = linkElement.getAttribute('data-link-id')
      const menuPanel = linkElement.closest('.js-header-mega-menu-panel')

      menuPanel.querySelectorAll('[data-image-id]').forEach((img) => {
        img.classList.remove('active')
      })

      const img = menuPanel.querySelector(`[data-image-id="${linkId}"]`)
      if (img !== null && img !== undefined) {
        menuPanel.classList.add('active-flyout')
        img.classList.add('active')
      }
    }
  }

  handleLinkMouseLeave(event) {
    const linkElement = event.target.closest('[data-link-id]')
    if (linkElement) {
      const menuPanel = linkElement.closest('.js-header-mega-menu-panel')
      menuPanel.classList.remove('active-flyout')
    }
  }

  openMenu(event) {
    try {
      event.preventDefault()
      this.clearExitTimer()
      this.activeMenuItem =
        event.target
          .closest('.js-header-mega-menu-parent')
        ?.querySelector('.js-header-mega-menu-panel') || null
    } catch (error) {
      this.activeMenuItem = null
    } finally {
      this.applyState()
    }
  }

  closeMenu(event) {
    if (event && !this.headerV2) {
      event.preventDefault()
    }
    this.setExitTimer()
  }

  handleMenuItemEnter() {
    this.clearExitTimer()
  }

  handleMenuItemLeave() {
    this.setExitTimer()
  }

  handleDocumentClick(event) {
    const clickInsideTrigger = event.target.closest(
      '.js-header-mega-menu-trigger'
    )
    const clickInsideMenuPanel = event.target.closest(
      '.js-header-mega-menu-panel'
    )

    if (!clickInsideTrigger && !clickInsideMenuPanel) {
      this.closeMenu(event)
      document.querySelectorAll('.active-group-menu').forEach((groupMenu) => {
        groupMenu.classList.remove('active-group-menu')
      })
    }
  }
  applyState() {
    let menuIsOpen = false
    for (const menuItem of this.menuItems) {
      if (this.activeMenuItem === menuItem) {
        menuItem.classList.add('active')
        this.headerV2 &&
          menuItem
            .closest('.js-header-mega-menu-parent')
            .classList.add('active-menu')
        menuItem.setAttribute('aria-hidden', false)
        menuItem.setAttribute('aria-expanded', true)
        menuIsOpen = true
        setTimeout(() => {
          menuItem.closest('ul').classList.add('active-group-menu')
        }, 500)
      } else {
        menuItem.classList.remove('active')
        this.headerV2 &&
          menuItem
            .closest('.js-header-mega-menu-parent')
            .classList.remove('active-menu')
        menuItem.setAttribute('aria-hidden', true)
        menuItem.setAttribute('aria-expanded', false)
      }
    }

    if (this.headerV2 && !this.isMobile()) {
      document.body.classList.toggle('overflow-hidden', menuIsOpen)
    }
  }

  isMobile() {
    return !window.matchMedia('(min-width: 768px)').matches
  }

  getOffsetTop() {
    const header = document.getElementById('main-header')
    return header ? header.offsetHeight : 0
  }

  closeOpenModals(e) {
    utils.all('hh-details-modal', this).map((element) => {
      if (element !== e.detail.value) {
        element.setAttribute('open', false)
      }
    })

    utils.all('header-search-details', this).map((element) => {
      if (element !== e.detail.value) {
        element.removeAttribute('open')
      }
    })

    utils.all('hh-header-submenu', this).map((element) => {
      if (element !== e.detail.value) {
        element.closeSubmenu()
      }
    })

    utils.all('hh-explore-collections-mobile', this).map((element) => {
      if (element !== e.detail.value) {
        element?.resetSideMenu()
      }
    })
  }

  toggleCart(e) {
    e?.detail?.event?.preventDefault()
    utils.toggle(document.body, ['mini-cart-is-active'])
  }

  openCart() {
    setTimeout(() => {
      document.body.classList.add('mini-cart-is-active')
    }, 500)
  }

  toggleAccount(e) {
    e?.detail?.event?.preventDefault()
    utils.toggle(document.body, ['account-header-modal-is-active'])
  }

  initPromoBar() {
    this.promoBar = document.querySelector('.promo-bar')
    if (this?.promoBar?.hasChildNodes()) {
      const promoCloseButton = this.promoBar.querySelector('button')
      if (sessionStorage.getItem('promoBarDismissed')) {
        this.promoBar.remove()
      } else {
        promoCloseButton.addEventListener('click', () => {
          this.promoBar.style.opacity = '0'
          setTimeout(() => {
            this.dismissPromoBar()
            const stickyHeader = document.querySelector('hh-sticky-header')
            stickyHeader.doSettings()
          }, 500)
        })
      }
    }
  }

  dismissPromoBar() {
    this.promoBar.remove()
    sessionStorage.setItem('promoBarDismissed', true)
  }

  handleHashedLinks() {
    const hashedLinks = this.querySelectorAll('a[href="#"]')

    hashedLinks?.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
      })
    })
  }

  observeDarkModeHeader() {
    if (!this.hasAttribute('data-main-header-dark')) {
      return
    }

    const desktopSubNavs = [...this.querySelectorAll('[data-mega-menu-subnav]')]
    const mobileMenuDrawer = this.querySelector('[data-mobile-menu]')
    const searchDrawer = this.querySelector('[data-header-search-details]')

    this.headerObserver = new MutationObserver(() => {
      const desktopSubNavIsOpen = desktopSubNavs.some((subNav) => subNav.classList.contains('active'))
      const mobileMenuIsOpen = mobileMenuDrawer?.hasAttribute('is-open') ?? false
      const searchDrawerIsOpen = searchDrawer?.hasAttribute('open') ?? false

      const isHeaderOpen = (
        desktopSubNavIsOpen ||
        mobileMenuIsOpen ||
        searchDrawerIsOpen
      )

      this.classList.toggle('hh-header--dark', !isHeaderOpen)
    })

    this.headerObserver.observe(this, {
      childList: true,
      attributes: true,
      subtree: true,
    })
  }

  render() {
    return html`<slot
      @modalOpened="${this.closeOpenModals}"
      @toggleCart="${this.toggleCart}"
      @toggleAccount="${this.toggleAccount}"
    ></slot>`
  }
}

customElements.define('hh-header', HHHeader)

class HHHeaderSubmenu extends LitElement {
  constructor() {
    super()

    this.detailsEl = this.querySelector('details')
    this.summaryEl = this.querySelector('summary')
    this.childEl = this.querySelector('.mobile-menu__submenu')
    this.closeButtonEl = this.querySelector('[data-close-details]')
    this.mobileBlockMenus = this.querySelectorAll('[data-mobile-menu-block-v2]')
  }

  connectedCallback() {
    super.connectedCallback()
    this.summaryEl?.addEventListener(
      'click',
      this.handleSummaryClick.bind(this)
    )
    this.closeButtonEl?.addEventListener('click', this.closeSubmenu.bind(this))

    if (this.mobileBlockMenus.length !== 0) {
      this.mobileBlockMenus.forEach((blockMenu) => {
        this.initMobileBlockMenus(blockMenu)
      })
    }
  }

  handleSummaryClick() {
    setTimeout(() => {
      this.detailsEl.classList.add('menu-opening')

      document.body.classList.add('submenu-active')
    }, 100)
  }

  closeSubmenu() {
    this.detailsEl.removeAttribute('open');
    this.detailsEl.classList.remove('menu-opening');

    document.body.classList.remove('submenu-active')
  }

  isAnyPartOfElementVisibleAndNotCovered(targetElement) {
    const rect = targetElement.getBoundingClientRect()
    const points = [
      { x: rect.left + 1, y: rect.top + 1 },
      { x: rect.right - 1, y: rect.top + 1 },
      { x: rect.left + 1, y: rect.bottom - 1 },
      { x: rect.right - 1, y: rect.bottom - 1 },
      { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
    ]

    for (const point of points) {
      if (
        point.x >= 0 &&
        point.y >= 0 &&
        point.x <=
          (window.innerWidth || document.documentElement.clientWidth) &&
        point.y <= (window.innerHeight || document.documentElement.clientHeight)
      ) {
        const topMostElem = document.elementFromPoint(point.x, point.y)
        if (
          topMostElem === targetElement ||
          targetElement.contains(topMostElem)
        ) {
          return true
        }
      }
    }

    return false
  }

  initMobileBlockMenus(blockMenu) {
    blockMenu.addEventListener('scroll', this.scrollCheck.bind(this, blockMenu))
    const stickyMobileSubmenuButtons = blockMenu.querySelectorAll(
      '[data-mobile-menu-sticky-list-button]'
    )
    stickyMobileSubmenuButtons.forEach((blockStickyButton, index) => {
      blockStickyButton.addEventListener(
        'click',
        this.stickyButtonClick.bind(this, blockStickyButton, blockMenu, index)
      )
    })
  }

  scrollCheck(blockMenu) {
    const stickyMobileSubmenuItems = blockMenu.querySelectorAll(
      '[data-mobile-menu-sticky-list-item]'
    )
    stickyMobileSubmenuItems.forEach((item) => {
      const scrollPercent = animationHelpers.scrollThroughPercentage(item)
      item.classList.toggle('top-sticky', scrollPercent > 50)
    })

    const afterStickyLists = blockMenu.querySelectorAll(
      '[data-mobile-menu-after-sticky-list]'
    )
    afterStickyLists.forEach((afterStickyList) => {
      const prevSiblingItem = afterStickyList.previousElementSibling
      prevSiblingItem?.classList.toggle(
        'list-in-view',
        this.isAnyPartOfElementVisibleAndNotCovered(afterStickyList)
      )
    })

    // Calculates bottom padding of last list so that the header finish stack at the top
    const [lastBlock] = [
      ...blockMenu.querySelectorAll('.header__submenu[data-parent-index]'),
    ].reverse()
    if (
      typeof lastBlock !== 'undefined' &&
      lastBlock.hasAttribute('data-mobile-menu-after-sticky-list') &&
      !lastBlock.hasAttribute('data-init')
    ) {
      lastBlock.style.paddingBottom = null

      const submenuHeaderHeight =
        document.querySelector('[data-submenu-header]')?.offsetHeight ?? 0
      const buttonsHeight = [
        ...this.querySelectorAll('[data-mobile-menu-sticky-list-button]'),
      ].reduce((total, button) => (total += button.offsetHeight ?? 0), 0)

      const heightToFill =
        window.innerHeight - (buttonsHeight + submenuHeaderHeight)
      const neededPadding = heightToFill - lastBlock.offsetHeight + 5

      lastBlock.style.paddingBottom = `${
        neededPadding > 0 ? neededPadding : 0
      }px`
      lastBlock.setAttribute('data-init', true)
    }
  }

  stickyButtonClick(blockStickyButton, blockMenu, index) {
    const stickyStop = 59 * index - 4 //Based on css calculations

    const scrollAmount = [
      ...blockMenu.querySelectorAll('.header__submenu[data-parent-index]'),
    ]
      .filter(
        (submenu) =>
          Number(blockStickyButton.dataset.parentIndex) >
          Number(submenu.dataset.parentIndex)
      )
      .reduce(
        (scrollTotal, submenu) => (scrollTotal += submenu.offsetHeight),
        0
      )

    blockMenu.scrollTo({
      top: scrollAmount - stickyStop,
      behavior: 'smooth'
    })
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('hh-header-submenu', HHHeaderSubmenu)
